import React from 'react'
import ReactDOM from 'react-dom/client'
import {
  PublicClientApplication,
  type RedirectRequest
} from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import {
  type DehydrateOptions,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import createClient from 'openapi-fetch'
import { ErrorMessage } from '../components/error-message/ErrorMessage'
import { type components, type paths as pathsProveV1 } from '../types/prove-v1'
import { disableNumberInputWheel } from '../utils/eventUtils'

type AppId = 'proveadmin' | 'provelab' | 'proveplan' | 'proveta'

type ClientConfig = components['schemas']['ClientConfig']

class BootstrapError extends Error {
  constructor(
    readonly stage: 'client-config' | 'msal',
    message: string,
    options?: ErrorOptions
  ) {
    super(message, options)
    this.name = 'BootstrapError'
  }
}

const applicationIdFields = {
  proveadmin: 'proveadminApplicationId',
  provelab: 'provelabApplicationId',
  proveplan: 'proveplanApplicationId',
  proveta: 'provetaApplicationId'
} satisfies Record<AppId, keyof ClientConfig>

interface BootstrapAppOptions {
  appId: AppId
  App: React.ComponentType<{ loginRequest: RedirectRequest }>
  baseUrl: string
  persistQueryKeys?: string[]
  startWorker: () => Promise<unknown>
  testRoles: string[]
  testUserName?: string
  onBeforeRender?: () => void
}

const getMsalClient = async ({
  appId,
  baseUrl,
  queryClient,
  startWorker,
  testRoles,
  testUserName
}: Pick<
  BootstrapAppOptions,
  'appId' | 'baseUrl' | 'startWorker' | 'testRoles' | 'testUserName'
> & { queryClient: QueryClient }) => {
  if (import.meta.env.MODE === 'test') {
    await startWorker()
    const { MsalReactTester } = await import('msal-react-tester')
    const msalTester = new MsalReactTester('Redirect', {
      idTokenClaims: {
        oid: '1234567890',
        roles: testRoles
      },
      homeAccountId: 'home-account-id',
      localAccountId: 'local-account-id',
      environment: 'login.windows.net',
      tenantId: 'tenant-id',
      username: 'test-account',
      name: testUserName ?? `Testbruker (${testRoles.join(', ')})`
    })

    await msalTester.isLogged()
    return {
      instance: msalTester.client,
      loginRequest: {
        redirectUri: `${globalThis.location.origin}/redirect.html`,
        scopes: ['test-scope']
      }
    }
  }

  const fetchClient = createClient<pathsProveV1>({ baseUrl })
  let data: ClientConfig
  try {
    data = await queryClient.fetchQuery({
      queryKey: ['client-config'],
      queryFn: async () => {
        const { data, error } = await fetchClient.GET('/client-config', {
          signal: AbortSignal.timeout(10_000)
        })
        if (error) throw new Error(error.detail)
        return data
      },
      // A single transient failure (e.g. a 500 during backend cold start) must
      // not kill app startup; retry the bootstrap config a few times first.
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000)
    })
  } catch (error) {
    throw new BootstrapError(
      'client-config',
      'Kunne ikke hente innloggingsoppsettet fra serveren.',
      { cause: error }
    )
  }

  const clientId = data[applicationIdFields[appId]]
  if (!clientId || !data.sampleTenantId || !data.sampleScope) {
    throw new BootstrapError(
      'client-config',
      'Serveren returnerte et ugyldig innloggingsoppsett.'
    )
  }

  let msalInstance: PublicClientApplication
  try {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: String(clientId),
        authority: `https://login.microsoftonline.com/${data.sampleTenantId}`
      },
      cache: {
        cacheLocation: 'localStorage'
      }
    })
    await msalInstance.initialize()
  } catch (error) {
    throw new BootstrapError(
      'msal',
      'Kunne ikke initialisere innloggingen. Prøv å laste siden på nytt.',
      { cause: error }
    )
  }

  return {
    instance: msalInstance,
    loginRequest: {
      redirectUri: `${globalThis.location.origin}/redirect.html`,
      scopes: [data.sampleScope]
    }
  }
}

const renderQueryProvider = (
  children: React.ReactNode,
  queryClient: QueryClient,
  persistQueryKeys: string[] | undefined
) => {
  if (!persistQueryKeys) {
    return (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    )
  }

  const persister = createAsyncStoragePersister({
    storage: globalThis.localStorage
  })
  const persistedKeys = new Set(persistQueryKeys)
  const dehydrateOptions: DehydrateOptions = {
    shouldDehydrateQuery: (query) =>
      persistedKeys.has(String(query.queryKey[0])),
    shouldDehydrateMutation: () => false
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        dehydrateOptions,
        maxAge: 1000 * 60 * 60 * 24 * 7 // 1 week
      }}
    >
      {children}
    </PersistQueryClientProvider>
  )
}

export const bootstrapApp = async ({
  appId,
  App,
  baseUrl,
  persistQueryKeys,
  startWorker,
  testRoles,
  testUserName,
  onBeforeRender
}: BootstrapAppOptions) => {
  disableNumberInputWheel()
  onBeforeRender?.()

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 60 * 1000, // 1 minute
        gcTime: 1000 * 60 * 60 * 24 * 7 // 1 week
      }
    }
  })

  try {
    const { instance, loginRequest } = await getMsalClient({
      appId,
      baseUrl,
      queryClient,
      startWorker,
      testRoles,
      testUserName
    })
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <MsalProvider instance={instance}>
        {renderQueryProvider(
          <App loginRequest={loginRequest} />,
          queryClient,
          persistQueryKeys
        )}
      </MsalProvider>
    )
  } catch (error) {
    const rootEl = document.getElementById('root')!
    const root = ReactDOM.createRoot(rootEl)
    root.render(
      <div className='p-4'>
        <ErrorMessage error={error} />
      </div>
    )
    const stage =
      error instanceof BootstrapError ? error.stage : 'unknown bootstrap stage'
    console.error(`Application bootstrap failed (${stage})`, error)
  }
}
